import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Group,
  PasswordInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    dni: '',
    phone_number: '',
    bio: '',
    skills: '',
    career: '',
    cuit_cuil: '',
    company_name: '',
    company_website: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });

  useEffect(() => {
    const saved = localStorage.getItem('portal-locale');
    if (saved && saved !== i18n.language) {
      i18n.changeLanguage(saved as 'en' | 'es');
    }
  }, [i18n]);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      dni: user.dni || '',
      phone_number: user.phone_number || '',
      bio: user.bio || '',
      skills: (user.skills || []).join(', '),
      career: user.career || '',
      cuit_cuil: user.cuit_cuil || '',
      company_name: user.company_name || '',
      company_website: user.company_website || '',
    });
  }, [user]);

  useEffect(() => {
    if (!success && !error) return;
    const timer = window.setTimeout(() => {
      setSuccess(false);
      setError(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [success, error]);

  const switchLang = (lng: 'en' | 'es') => {
    i18n.changeLanguage(lng);
    localStorage.setItem('portal-locale', lng);
  };

  const isJobPoster = user?.user_type === 'JOB_POSTER';
  const isJobSeeker = user?.user_type === 'JOB_SEEKER';
  const careerOptions = [
    { value: 'MUSICOTERAPIA', label: t('careers.musicotherapy') },
    { value: 'PSICOLOGIA', label: t('careers.psychology') },
    { value: 'PROFESORADO_PSICOLOGIA', label: t('careers.psychologyTeaching') },
    { value: 'TERAPIA_OCUPACIONAL', label: t('careers.occupationalTherapy') },
  ];

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      const skillsArray = form.skills
        .split(',')
        .map((skill) => skill.trim())
        .filter((skill) => skill.length > 0);

      const trimmedFirstName = form.first_name.trim();
      if (!trimmedFirstName) {
        setError(t('register.firstNameRequired'));
        setSaving(false);
        return;
      }

      const trimmedLastName = form.last_name.trim();
      if (!trimmedLastName) {
        setError(t('register.lastNameRequired'));
        setSaving(false);
        return;
      }

      const trimmedDni = form.dni.trim();
      if (!trimmedDni) {
        setError(t('register.dniRequired'));
        setSaving(false);
        return;
      }
      if (!/^\d{6,8}$/.test(trimmedDni)) {
        setError(t('register.dniInvalid'));
        setSaving(false);
        return;
      }
      if (isJobSeeker && !form.career) {
        setError(t('register.careerRequired'));
        setSaving(false);
        return;
      }

      const trimmedPhone = form.phone_number.trim();
      if (!trimmedPhone) {
        setError(t('register.phoneRequired'));
        setSaving(false);
        return;
      }
      if (!/^\d{10,}$/.test(trimmedPhone)) {
        setError(t('register.phoneInvalid'));
        setSaving(false);
        return;
      }
      if (/^(\d)\1+$/.test(trimmedPhone) || trimmedPhone === '11111111111') {
        setError(t('register.phoneInvalid'));
        setSaving(false);
        return;
      }

      const trimmedCuit = form.cuit_cuil.trim();
      if (isJobPoster && trimmedCuit && !/^\d{11}$/.test(trimmedCuit)) {
        setError(t('register.cuitCuilNumberInvalid'));
        setSaving(false);
        return;
      }

      await api.updateUser(user.id, {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        dni: trimmedDni,
        phone_number: trimmedPhone,
        bio: form.bio,
        skills: skillsArray,
        career: isJobSeeker ? form.career : undefined,
        cuit_cuil: isJobPoster ? trimmedCuit : undefined,
        company_name: isJobPoster ? form.company_name : undefined,
        company_website: isJobPoster ? form.company_website : undefined,
      });

      await refreshUser();
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('users.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setPasswordSaving(true);
      setPasswordError(null);
      setPasswordSuccess(false);

      if (passwordForm.next.length < 8) {
        setPasswordError(t('profile.password.tooShort'));
        setPasswordSaving(false);
        return;
      }
      if (passwordForm.next !== passwordForm.confirm) {
        setPasswordError(t('profile.password.mismatch'));
        setPasswordSaving(false);
        return;
      }

      await api.changeMyPassword({
        old_password: passwordForm.current,
        new_password: passwordForm.next,
        new_password_confirm: passwordForm.confirm,
      });

      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordSuccess(true);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : t('profile.password.mismatch'));
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Card withBorder padding="xl" radius="lg" shadow="sm">
      <Stack gap="sm">
        <Title order={2}>{t('profile.title')}</Title>
        <Text c="dimmed">{t('profile.subtitle')}</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="sm">
          <div>
            <Text c="dimmed" fz="sm">{t('profile.username')}</Text>
            <Text fw={600}>{user?.email || '—'}</Text>
          </div>
          <div>
            <Text c="dimmed" fz="sm">{t('profile.email')}</Text>
            <Text fw={600}>{user?.email || '—'}</Text>
          </div>
          <div>
            <Text c="dimmed" fz="sm">{t('profile.role')}</Text>
            <Text fw={600}>
              {user?.user_type === 'JOB_SEEKER' && t('common.roles.jobSeeker')}
              {user?.user_type === 'JOB_POSTER' && t('common.roles.jobPoster')}
              {user?.user_type === 'MODERATOR' && t('common.roles.moderator')}
              {user?.user_type === 'ADMIN' && t('common.roles.admin')}
            </Text>
          </div>
          <div>
            <Text c="dimmed" fz="sm">{t('profile.approved')}</Text>
            <Text fw={600}>{user?.is_approved ? t('profile.approvedYes') : t('profile.approvedPending')}</Text>
          </div>
        </SimpleGrid>

        <Stack gap="md" mt="lg">
          <Title order={4}>{t('profile.editTitle')}</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label={t('profile.fields.firstName')}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              required
            />
            <TextInput
              label={t('profile.fields.lastName')}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              required
            />
            {isJobSeeker && (
              <Select
                label={t('profile.fields.career')}
                data={careerOptions}
                value={form.career}
                onChange={(value) => setForm({ ...form, career: value || '' })}
                placeholder={t('register.careerPlaceholder')}
                required
              />
            )}
            <TextInput
              label={t('profile.fields.dni')}
              value={form.dni}
              onChange={(e) => setForm({ ...form, dni: e.target.value })}
              required
            />
            <TextInput
              label={t('profile.fields.phone')}
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              required
            />
            {isJobPoster && (
              <TextInput
                label={t('profile.fields.cuitCuil')}
                value={form.cuit_cuil}
                onChange={(e) => setForm({ ...form, cuit_cuil: e.target.value })}
              />
            )}
            {isJobPoster && (
              <TextInput
                label={t('profile.fields.companyName')}
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              />
            )}
            {isJobPoster && (
              <TextInput
                label={t('profile.fields.companyWebsite')}
                value={form.company_website}
                onChange={(e) => setForm({ ...form, company_website: e.target.value })}
              />
            )}
          </SimpleGrid>
          <Textarea
            label={t('profile.fields.bio')}
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            minRows={3}
          />
          <TextInput
            label={t('profile.fields.skills')}
            value={form.skills}
            onChange={(e) => setForm({ ...form, skills: e.target.value })}
            description={t('profile.fields.skillsHint')}
          />
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="sm" align="center" wrap="wrap">
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert icon={<IconCheck size={16} />} title="Success" color="green" radius="md">
                  {t('profile.updated')}
                </Alert>
              )}
            </Group>
            <Button onClick={handleSave} loading={saving}>
              {t('profile.save')}
            </Button>
          </Group>
        </Stack>

        <Stack gap="md" mt="lg">
          <Title order={4}>{t('profile.password.title')}</Title>
          {passwordSuccess && (
            <Alert icon={<IconCheck size={16} />} title="Success" color="green" radius="md">
              {t('profile.password.updated')}
            </Alert>
          )}
          {passwordError && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" radius="md">
              {passwordError}
            </Alert>
          )}
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <PasswordInput
              label={t('profile.password.current')}
              value={passwordForm.current}
              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
            />
            <PasswordInput
              label={t('profile.password.new')}
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
            />
            <PasswordInput
              label={t('profile.password.confirm')}
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
            />
          </SimpleGrid>
          <Group justify="flex-end">
            <Button onClick={handlePasswordChange} loading={passwordSaving}>
              {t('profile.password.save')}
            </Button>
          </Group>
        </Stack>

        <Group gap="md" mt="lg" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <div>
            <Text c="dimmed" fz="sm" mb="xs">Language</Text>
            <SegmentedControl
              size="sm"
              value={i18n.language.startsWith('es') ? 'es' : 'en'}
              onChange={(value) => switchLang(value as 'en' | 'es')}
              data={[
                { label: 'English', value: 'en' },
                { label: 'Español', value: 'es' }
              ]}
              color="plum"
              radius="md"
            />
          </div>
        </Group>
      </Stack>
    </Card>
  );
};

export default ProfilePage;
